import { ReactNode, ButtonHTMLAttributes } from 'react';
import cn from '../../../utils/classNames';
import useResponsive from '../../../hooks/useResponsive';
import styles from './Button.module.css';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: "lg" | "md" | "sm";
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'success' | 'error';
  icon?: ReactNode;
  iconPosition?: 'prefix' | 'suffix';
  loading?: boolean;
  children?: ReactNode;
  fullWidth?: boolean;
  responsive?: boolean;
  submit?: boolean;
  compact?: boolean;
}

const Button = (props: ButtonProps) => {
  const {
    size,
    variant = 'primary',
    icon,
    iconPosition = 'prefix',
    loading = false,
    children,
    fullWidth = false,
    responsive = false,
    submit = false,
    compact = false,
    className,
    disabled,
    onClick,
    ...rest
  } = props;

  const responsiveState = useResponsive();
  const isDisabled = disabled || loading;

  const getDisplayMode = () => {
    if (!responsive || !icon) return 'both';
    
    const { windowWidth } = responsiveState;
    
    if (windowWidth >= 1024) return 'both';    
    if (windowWidth >= 768) return 'text';     
    if (windowWidth >= 640) return 'icon';     
    return 'icon';                             
  };

  const displayMode = getDisplayMode();
  const showIcon = (displayMode === 'both' || displayMode === 'icon') && !!icon;
  const showText = (displayMode === 'both' || displayMode === 'text') && !!children;

  const buttonClass = cn(
    styles.btn,
    size && styles[`size${size.charAt(0).toUpperCase() + size.slice(1)}`],
    styles[variant],
    {
      [styles.loading]: loading,
      [styles.fullWidth]: fullWidth,
      [styles.responsive]: responsive && !!icon,
      [styles.iconOnly]: showIcon && !showText,
      [styles.textOnly]: showText && !showIcon,
      [styles.compact]: compact,
    },
    className
  );

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled && onClick) {
      onClick(e);
    }
  };

  const buttonType = submit ? 'submit' : 'button';

  const renderIcon = () => {
    if (loading) {
      return <span className={styles.loader} />;
    }
    
    if (showIcon && icon) {
      return <span className={styles.icon}>{icon}</span>;
    }
    
    return null;
  };

  const renderText = () => {
    if (showText) {
      return <span className={styles.text}>{children}</span>;
    }
    
    return <span className={styles.visuallyHidden}>{children}</span>;
  };

  return (
    <button
      type={buttonType}
      className={`toggleButton ${buttonClass}`}
      disabled={isDisabled}
      onClick={handleClick}
      {...rest}
    >
      <span className={styles.content}>
        {iconPosition === 'prefix' && renderIcon()}
        {renderText()}
        {iconPosition === 'suffix' && renderIcon()}
      </span>
    </button>
  );
};

export default Button;